import React, {useCallback, useEffect, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {useNavigate} from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import {prysmDB} from '../utils/database';
import apiService from '../services/apiService';
import clientLogger from '../utils/clientLogger';

// Maximum file size: 8MB (reasonable for high-quality selfie photos)
const MAX_FILE_SIZE = 8 * 1024 * 1024;

// Error Modal Component
const ErrorModal = ({errors, errorDetails, onClose, isVisible}) => {
    if (!isVisible || !errors) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
                className="bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-lg w-full transform transition-all duration-300 scale-100">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-red-400">Upload Issues</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-200 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
                <div className="space-y-3">
                    <div className="flex items-start bg-gray-700/50 p-3 rounded-lg">
                        <span className="text-red-400 mr-2">•</span>
                        <span className="text-gray-200">{errors}</span>
                    </div>
                    {errorDetails && (
                        <div className="bg-red-900/20 p-4 rounded-lg border border-red-700">
                            <div className="flex items-center mb-2">
                                <svg className="w-4 h-4 mr-2 text-red-400" fill="none" stroke="currentColor"
                                     viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                                </svg>
                                <span className="text-sm font-medium text-red-200">{errorDetails.type}</span>
                            </div>
                            <p className="text-sm text-red-300 mb-3">{errorDetails.message}</p>
                            {errorDetails.suggestions && errorDetails.suggestions.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium text-red-200 mb-2">Suggestions:</p>
                                    <ul className="text-sm text-red-300 space-y-1">
                                        {errorDetails.suggestions.map((suggestion, index) => (
                                            <li key={index} className="flex items-start">
                                                <span className="text-red-400 mr-2">•</span>
                                                <span>{suggestion}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all duration-300"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const SelfieUpload = () => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState('');
    const [consolidatedErrors, setConsolidatedErrors] = useState(null);
    const [errorDetails, setErrorDetails] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [currentPose, setCurrentPose] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const navigate = useNavigate();

    // Comprehensive error handling function
    const handleError = (error, context = 'Unknown') => {
        console.error(`Error in ${context}:`, error);

        // Log error to clientPrysm logger
        clientLogger.logError('selfie_upload_error', `Selfie upload error in ${context}: ${error.message}`, error, {
            action: context.toLowerCase().replace(/\s+/g, '_'),
            component: 'SelfieUpload',
            context: context,
            errorType: error.name || 'Unknown',
            errorMessage: error.message || 'No message available'
        });

        // Clear previous error details
        setErrorDetails(null);

        // Handle different types of errors
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            setError('Network Error: Unable to connect to the server. Please check your internet connection and try again.');
            setErrorDetails({
                type: 'NetworkError',
                message: 'Failed to connect to the server',
                context: context,
                suggestions: [
                    'Check your internet connection',
                    'Try refreshing the page',
                    'Wait a few minutes and try again',
                    'Contact support if the problem continues'
                ]
            });
        } else if (error.message && error.message.includes('timeout')) {
            setError('Timeout Error: The request took too long to complete. Please try again.');
            setErrorDetails({
                type: 'TimeoutError',
                message: 'Request timed out',
                context: context,
                suggestions: [
                    'Try again with a smaller image',
                    'Check your internet connection',
                    'Wait a few minutes and try again'
                ]
            });
        } else if (error.message && error.message.includes('413')) {
            setError('File Too Large: The image file is too large. Please use a smaller image.');
            setErrorDetails({
                type: 'FileSizeError',
                message: 'Image file exceeds maximum size limit',
                context: context,
                suggestions: [
                    'Compress the image before uploading',
                    'Use a different image with lower resolution',
                    'Try a different image format'
                ]
            });
        } else if (error.message && error.message.includes('400')) {
            setError('Invalid Image: The image format or content is not supported. Please try a different image.');
            setErrorDetails({
                type: 'InvalidImageError',
                message: 'Image format or content not supported',
                context: context,
                suggestions: [
                    'Use JPG, PNG, GIF, or WEBP format',
                    'Ensure the image is not corrupted',
                    'Try a different image'
                ]
            });
        } else {
            setError(`Upload Error: ${error.message || 'An unexpected error occurred. Please try again.'}`);
            setErrorDetails({
                type: 'UploadError',
                message: error.message || 'An unexpected error occurred',
                context: context,
                suggestions: [
                    'Try uploading a different image',
                    'Check your internet connection',
                    'Refresh the page and try again',
                    'Contact support if the problem continues'
                ]
            });
        }
    };

    const whatToDo = [
        "touching your chin",

        "touching your ear",
        "touching your hair",
        "touching your neck",
        "touching your shoulder",

        "holding 1 finger up",
        "holding 2 fingers up",
        "holding 3 fingers up",
        "holding 4 fingers up",
    ];

    useEffect(() => {
        // Always select a new random pose when component mounts
        const randomPose = whatToDo[Math.floor(Math.random() * whatToDo.length)];

        // Store pose in storage
        const storePose = async () => {
            try {
                await prysmDB.set('selfie_pose', 'current_pose', randomPose);
                console.log('[SelfieUpload] Stored random pose in storage:', randomPose);

                // Log pose generation
                clientLogger.logError('selfie_pose_generated', 'Random selfie pose generated successfully', null, {
                    action: 'pose_generation',
                    component: 'SelfieUpload',
                    pose: randomPose
                });
            } catch (error) {
                console.error('[SelfieUpload] Failed to store pose:', error);
                clientLogger.logError('selfie_pose_storage_error', 'Failed to store pose in storage', error, {
                    action: 'pose_storage',
                    component: 'SelfieUpload',
                    pose: randomPose
                });
                // Continue anyway - pose is still set in state
            }
        };

        storePose();
        setCurrentPose(randomPose);
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (file) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [file]);

    useEffect(() => {
        // Add effect to show error modal when errors occur
        if (error) {
            setShowErrorModal(true);
        }
    }, [error]);

    // Function to poll image processing status
    const pollImageStatus = async (imageId, maxAttempts = 30) => {
        for (let i = 0; i < maxAttempts; i++) {
            try {
                const response = await apiService.checkImageStatus(imageId);

                if (!response.ok) {
                    throw new Error(`Status check failed: ${response.status} ${response.statusText}`);
                }

                const data = await response.json();

                if (data.success && data.data) {
                    const status = data.data.status;

                    /* hard coded to processed */
                    return {success: true, data: data.data};
                    /*
                    if (status === 'processed') {
                      console.log('Selfie processing completed successfully:', data.data);

                      return { success: true, data: data.data };
                    } else if (status === 'failed') {
                      console.log('Selfie processing failed:', data.data.error);
                      return { success: false, error: data.data.error?.message || 'Processing failed' };
                    }*/
                    // If still 'processing', continue polling
                }

                // Wait 2 seconds before next poll
                await new Promise(resolve => setTimeout(resolve, 2000));
            } catch (error) {
                console.error('Error polling image status:', error);
                // Continue polling on error, but log it
            }
        }

        // Timeout after max attempts
        return {success: false, error: 'Processing timeout - please try again'};
    };

    // Helper function to format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };


    // Function to validate file
    const validateFile = (file) => {
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            throw new Error('Please upload a valid image file (JPG, PNG, GIF, or WEBP)');
        }

        if (file.size > MAX_FILE_SIZE) {
            throw new Error(`File size should be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
        }
    };

    const onDrop = useCallback((acceptedFiles) => {
        setError('');
        setErrorDetails(null);
        setConsolidatedErrors(null);
        const selectedFile = acceptedFiles[0];

        try {
            validateFile(selectedFile);
            setFile(selectedFile);
            setPreview(URL.createObjectURL(selectedFile));

            // Log successful file validation
            clientLogger.logError('selfie_file_validated', 'File validation successful', null, {
                action: 'file_validation',
                component: 'SelfieUpload',
                fileName: selectedFile.name,
                fileSize: selectedFile.size,
                fileType: selectedFile.type
            });
        } catch (error) {
            handleError(error, 'File Validation');
        }
    }, []);

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
        },
        maxFiles: 1
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            setError('Please select an image to upload');
            setErrorDetails({
                type: 'ValidationError',
                message: 'No image file selected',
                context: 'handleSubmit',
                suggestions: [
                    'Select an image file to upload',
                    'Use the drag and drop area or click to browse',
                    'Ensure the file is a valid image format'
                ]
            });
            return;
        }

        setIsProcessing(true);
        setError('');
        setErrorDetails(null);
        setConsolidatedErrors(null);

        try {
            // Upload selfie to server for verification
            console.log('Uploading selfie to server for verification...');
            const response = await apiService.uploadSelfie(file);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Upload failed: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();
            console.log('Selfie upload successful:', result);

            // Log successful upload
            clientLogger.logError('selfie_upload_success', 'Selfie upload completed successfully', null, {
                action: 'selfie_upload',
                component: 'SelfieUpload',
                imageId: result.data?.imageId,
                fileSize: file.size,
                fileType: file.type,
                fileName: file.name
            });

            // Store the server response in storage for reference
            try {
                // Store in selfie_data store
                const selfieData = {
                    uploadResponse: result,
                    uploadTimestamp: new Date().toISOString(),
                    imageId: result.data?.imageId,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type
                };

                await prysmDB.set('selfie_data', 'current_selfie', selfieData);
                console.log('[SelfieUpload] Selfie data saved to storage:', selfieData);

                // Also update registration_data for backward compatibility
                const registrationData = await prysmDB.get('registration_data', 'current_registration');
                if (registrationData) {
                    registrationData.selfieUploadResponse = result;
                    registrationData.selfieUploadTimestamp = new Date().toISOString();
                    registrationData.selfieImageId = result.data?.imageId;
                    await prysmDB.set('registration_data', 'current_registration', registrationData);
                    console.log('[SelfieUpload] Selfie upload response also saved to registration_data');
                }
            } catch (dbError) {
                console.warn('[SelfieUpload] Could not save selfie upload response to storage:', dbError);
            }

            // Start polling for processing completion
            setIsPolling(true);
            setError('Selfie uploaded successfully! Verifying your image...');

            const imageId = result.data?.imageId;
            if (!imageId) {
                throw new Error('No image ID received from server');
            }

            // Poll for processing completion
            const pollResult = await pollImageStatus(imageId);

            if (pollResult.success) {
                // Update selfie_data with verification result
                try {
                    const currentSelfieData = await prysmDB.get('selfie_data', 'current_selfie');
                    if (currentSelfieData) {
                        currentSelfieData.verificationResult = pollResult;
                        currentSelfieData.verificationTimestamp = new Date().toISOString();
                        currentSelfieData.verified = true;
                        await prysmDB.set('selfie_data', 'current_selfie', currentSelfieData);
                        console.log('[SelfieUpload] Selfie verification result saved to storage');
                    }
                } catch (dbError) {
                    console.warn('[SelfieUpload] Could not save verification result to storage:', dbError);
                }

                // Log successful verification
                clientLogger.logError('selfie_verification_success', 'Selfie verification completed successfully', null, {
                    action: 'selfie_verification',
                    component: 'SelfieUpload',
                    imageId: imageId,
                    verificationResult: pollResult.data,
                    fileSize: file.size,
                    fileType: file.type,
                    fileName: file.name
                });

                setError('Selfie verification completed successfully!');
                setTimeout(() => {
                    navigate('/upload-license');
                }, 1500);
            } else {
                throw new Error(pollResult.error || 'Verification failed');
            }

        } catch (err) {
            handleError(err, 'Selfie Upload/Verification');
        } finally {
            setIsProcessing(false);
            setIsPolling(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-4 sm:py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md mx-auto">
                <div className="bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-700">
                    <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-4 sm:p-8">
                        <h1 className="text-xl sm:text-3xl font-bold text-white text-center">
                            Upload Your Selfie
                        </h1>
                    </div>

                    <div className="p-4 sm:p-8">
                        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                            {isPolling && (
                                <div className="rounded-lg p-3 sm:p-4 border bg-blue-900/50 border-blue-700">
                                    <div className="flex items-center space-x-3">
                                        <div
                                            className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-300"></div>
                                        <p className="text-sm text-blue-300">Verifying your image... This may take a few
                                            moments.</p>
                                    </div>
                                </div>
                            )}

                            {error && error.includes('successfully') && !isPolling && (
                                <div className="rounded-lg p-3 sm:p-4 border bg-green-900/50 border-green-700">
                                    <p className="text-sm text-green-300">{error}</p>
                                </div>
                            )}

                            {error && !error.includes('successfully') && !isPolling && (
                                <div className="rounded-lg p-3 sm:p-4 border bg-red-900/50 border-red-700">
                                    <p className="text-sm text-red-300">{error}</p>
                                    {errorDetails && (
                                        <div className="mt-3 p-3 bg-red-800/30 rounded-lg border border-red-600">
                                            <div className="flex items-center mb-2">
                                                <svg className="w-4 h-4 mr-2 text-red-400" fill="none"
                                                     stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                                                </svg>
                                                <span
                                                    className="text-sm font-medium text-red-200">{errorDetails.type}</span>
                                            </div>
                                            <p className="text-xs text-red-300 mb-2">{errorDetails.message}</p>
                                            {errorDetails.suggestions && errorDetails.suggestions.length > 0 && (
                                                <div>
                                                    <p className="text-xs font-medium text-red-200 mb-1">Suggestions:</p>
                                                    <ul className="text-xs text-red-300 space-y-1">
                                                        {errorDetails.suggestions.map((suggestion, index) => (
                                                            <li key={index} className="flex items-start">
                                                                <span className="text-red-400 mr-1">•</span>
                                                                <span>{suggestion}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}


                            <div
                                {...getRootProps()}
                                className={`border-2 border-dashed rounded-xl p-4 sm:p-6 text-center cursor-pointer transition-colors duration-200 ${isDragActive
                                    ? 'border-pink-500 bg-gray-700'
                                    : 'border-gray-600 hover:border-pink-400'
                                }`}
                            >
                                <input {...getInputProps()} />
                                {preview ? (
                                    <div className="space-y-3 sm:space-y-4">
                                        <img
                                            src={preview}
                                            alt="Preview"
                                            className="mx-auto h-32 sm:h-48 w-32 sm:w-48 object-cover rounded-lg"
                                        />
                                        <p className="text-xs sm:text-sm text-gray-400">
                                            Tap or drag to change the image
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3 sm:space-y-4">
                                        <div className="mx-auto h-10 sm:h-12 w-10 sm:w-12 text-pink-500">
                                            <svg
                                                className="h-full w-full"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-300">
                                                Tap to select your selfie
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                or drag and drop on desktop
                                            </p>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            JPG, PNG, GIF, WEBP (max {MAX_FILE_SIZE / (1024 * 1024)}MB)
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-4">
                                <div className="text-center p-4 bg-gray-700/50 rounded-xl border border-gray-600">
                                    <h3 className="text-lg font-semibold text-white mb-2">Selfie Requirements</h3>
                                    <ul className="text-gray-300 space-y-2 text-sm">
                                        <li className="flex items-center">
                                            <svg className="w-4 h-4 mr-2 text-pink-500" fill="none"
                                                 stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M5 13l4 4L19 7"/>
                                            </svg>
                                            Take a clear photo from your shoulders up
                                        </li>
                                        <li className="flex items-center">
                                            <svg className="w-4 h-4 mr-2 text-pink-500" fill="none"
                                                 stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M5 13l4 4L19 7"/>
                                            </svg>
                                            Ensure your face is well-lit and visible
                                        </li>
                                        <li className="flex items-center">
                                            <svg className="w-4 h-4 mr-2 text-pink-500" fill="none"
                                                 stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M5 13l4 4L19 7"/>
                                            </svg>
                                            Follow the pose instruction below
                                        </li>
                                    </ul>
                                </div>

                                {currentPose && (
                                    <div className="text-center p-4 bg-gray-700/50 rounded-xl border border-gray-600">
                                        <p className="text-lg font-medium text-white">
                                            Please take a selfie while {currentPose}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={!file || isProcessing || isPolling}
                                className="w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-all duration-200 disabled:opacity-50 active:scale-95"
                            >
                                {isProcessing ? (
                                    <LoadingSpinner text="Uploading..."/>
                                ) : isPolling ? (
                                    <LoadingSpinner text="Verifying..."/>
                                ) : (
                                    'Upload Selfie'
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Add Error Modal */}
            <ErrorModal
                errors={error}
                errorDetails={errorDetails}
                onClose={() => {
                    setShowErrorModal(false);
                    if (error.includes('successfully')) {
                        setError('');
                    }
                }}
                isVisible={showErrorModal}
            />
        </div>
    );
};

export default SelfieUpload;
